<template>
  <LtFullScreen ref="fullScreenRef">
    <div
      ref="containerRef"
      :class="[
        ns.b('container'),
        {
          [ns.b('readonly')]: mode === 'readonly',
          [ns.b('editonly')]: mode === 'editonly',
          [ns.b('dark')]: isDarkMode,
        },
      ]"
      @paste="onPaste"
    >
      <div :class="ns.b('toolbar')">
        <Toolbar />
      </div>
      <div
        :class="[
          ns.b('content-container'),
          { 'hide-preview': hidePreviewView },
        ]"
        :style="{ height: `${editorContainerHeight}px` }"
      >
        <div :class="ns.b('editor')">
          <textarea
            ref="editorRef"
            :placeholder="placeholder"
            :model-value="finalModelValue"
          />

          <lt-popover
            ref="overlayRef"
            v-model:visible="isHintShow"
            :virtual-ref="cursorRef"
            virtual-triggering
            placement="bottom-start"
            @click="withModifiers(() => {}, ['stop'])"
          >
            <slot name="hintTemplate" />
          </lt-popover>

          <div v-if="Boolean(maxlength)" :class="ns.b('count')">
            {{ (finalModelValue.length || 0) / maxlength }}
          </div>
        </div>

        <MdRender
          ref="renderRef"
          :base-url="baseUrl"
          :breaks="breaks"
          :content="finalModelValue"
          :custom-parse="customParse"
          :render-parse="renderParse"
          :md-rules="mdRules"
          :custom-renderer-rules="customRendererRules"
          :custom-xss-rules="customXssRules"
          disable-render
          :md-plugins="mdPlugins"
          @mouseover="onPreviewMouseover"
          @mouseout="onPreviewMouseout"
          @scroll="onPreviewScroll"
          @md-render-change="previewContentChange"
          @checked-change="onChecked"
        >
          <div
            v-for="(html, index) in previewHtmlList || []"
            :key="index"
            :innerHTML="html"
          />
        </MdRender>
      </div>
    </div>
  </LtFullScreen>
</template>

<script lang="ts" setup>
import { computed, provide, ref, watch, withModifiers } from 'vue'
import LtFullScreen from '@lit-element/components/fullScreen'
import LtPopover from '@lit-element/components/popover'
import { useNamespace } from '@lit-element/hooks'
import { EditorMdInjectionKey, editorMdProps } from './editor-md-types'
import Toolbar from './components/toolbar'
import MdRender from './components/render-md.vue'
import { locale } from './utils'
import { useEditorMdTheme } from './composables/use-editor-md-theme'
import { useEditorMd } from './composables/use-editor-md'

defineOptions({ name: 'LtEditorMd' })
const props = defineProps(editorMdProps)
const emits = defineEmits([
  'update:modelValue',
  'checkedChange',
  'selectHint',
  'afterEditorInit',
  'contentChange',
  'previewContentChange',
  'imageUpload',
])

const ns = useNamespace('md')

const showFullscreen = ref(false)
const fullScreenRef = ref()

const finalModelValue = computed(() => {
  if (typeof props.maxlength === 'number') {
    return props.modelValue.slice(0, Math.max(0, props.maxlength))
  } else {
    return props.modelValue
  }
})

const {
  editorRef,
  overlayRef,
  cursorRef,
  renderRef,
  containerRef,
  isHintShow,
  toolbars,
  previewHtmlList,
  onPaste,
  getEditorIns,
  previewContentChange,
  onChecked,
  onPreviewScroll,
  onPreviewMouseout,
  onPreviewMouseover,
} = useEditorMd(props, emits)

const { isDarkMode } = useEditorMdTheme(() => null)

watch(showFullscreen, () => {
  fullScreenRef.value?.toggleMode('onlyShowContainer')
})

provide(EditorMdInjectionKey, {
  showFullscreen,
  toolbars,
  toolbarConfig: props.toolbarConfig,
  customToolbars: props.customToolbars,
  getEditorIns,
  t: locale,
})
</script>
